import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { AddCircle, ExportSquare } from 'iconsax-react';
import { MoreHoriz } from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import HTimePicker from '@ui/components/common/Pickers/HTimePicker';
import GenericAvatarGroup from '@ui/components/common/GenericAvatarGroup';
import { StyledMenu } from '@ui/components/common/Menu';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import ClosePickupLocationDialog from './ClosePickupLocationDialog';
import NewPickupLocationDialog from './NewPickupLocationDialog';
import AssignPickupLocationCoordinatorDialog from './AssignPickupLocationCoordinatorDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAllPickupLocationsAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { FamilyFestGetPickupLocationsPaginatedAsync, FamilyFestAssignCoordinatorAsync, FamilyFestUnassignCoordinatorAsync, downloadFamilyFestPickupLocationsExcelAsync, downloadFamilyFestPickupLocationCoordinatorsExcelAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { getAllFamilyFestPickupAssignmentsAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
import { Iconly } from 'react-iconly';
import { PickUpLocationStatusEnum } from '@application/enums/pickuplocation-status-enum';
import { getVDKEventByIdForEventDetailsAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { EventDocumentType } from '@application/enums/event-document-type';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import { formatDMY } from '@application/utilities/date-time-formater';
const LocationContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [, setSearchParams] = useSearchParams();
    const { eventId } = useParams();
    const [searchValue, setSearchValue] = useState('');
    const [filters, setFilters] = useState({});
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
    const [isNewPickupDialogOpen, setIsNewPickupDialogOpen] = useState(false);
    const [selectedPickupLocation, setSelectedPickupLocation] = useState('');
    const [selectedPickupLocationId, setSelectedPickupLocationId] = useState('');
    const [editingPickupLocation, setEditingPickupLocation] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [coordinatorActionTarget, setCoordinatorActionTarget] = useState(null);
    const [isAssignCoordinatorDialogOpen, setIsAssignCoordinatorDialogOpen] = useState(false);
    const [coordinatorAttendeeRows, setCoordinatorAttendeeRows] = useState([]);
    const [coordinatorAttendeesLoading, setCoordinatorAttendeesLoading] = useState(false);
    const [coordinatorSearch, setCoordinatorSearch] = useState('');
    const [debouncedCoordinatorSearch, setDebouncedCoordinatorSearch] = useState('');
    const [confirmUnassignCoordinatorOpen, setConfirmUnassignCoordinatorOpen] = useState(false);
    const [isExportingCoordinators, setIsExportingCoordinators] = useState(false);
    const [isExportingPickupLocations, setIsExportingPickupLocations] = useState(false);
    const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
    const { pickupLocations: standardPickupLocations, pickupLocationResponse, status: standardPickupStatus } = useAppSelector((state) => state.pickupLocation);
    const { pickupLocationsPaginated: familyFestPickupLocations, familyFestCloseResponse } = useAppSelector((state) => state.familyFestPickupLocation);
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const isFamilyFest = VDKEventsForEventDetails?.type === EventTypeEnum.Family;
    const pickupLocations = isFamilyFest ? familyFestPickupLocations : standardPickupLocations;
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    // Pickup locations are scoped by the selected companies via the auth token,
    // so the list must be refetched whenever the sidebar company selection changes.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const isTransportManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('TransportManager');
    const isExternalTransportManager = isTransportManager &&
        Number(currentUser?.CollaboratorAccessLevel) === CollaboratorAccessLevel.External;
    const dispatch = useAppDispatch();
    // Fetch VDKEvent data if needed
    useEffect(() => {
        if (!eventId || VDKEventsForEventDetails?.id === eventId) {
            return;
        }
        dispatch(getVDKEventByIdForEventDetailsAsync({
            id: eventId,
            documentType: EventDocumentType.BackgroundImage
        }));
    }, [dispatch, eventId, VDKEventsForEventDetails?.id]);
    // Get companies and suppliers from VdkEvent like in NewPickupLocationDialog
    const companyFilterOptions = useMemo(() => {
        return (VDKEventsForEventDetails?.eventCompanies ?? []).map((company) => ({
            value: company.value,
            label: company.label
        }));
    }, [VDKEventsForEventDetails?.eventCompanies]);
    const supplierFilterOptions = useMemo(() => {
        return (VDKEventsForEventDetails?.assignedCollaborators ?? []).map((collaborator) => ({
            value: collaborator.id,
            label: `${collaborator.firstName} ${collaborator.lastName}`.trim()
        }));
    }, [VDKEventsForEventDetails?.assignedCollaborators]);
    const accommodationFilterOptions = useMemo(() => [
        { value: true, label: t('common.yes') || 'Yes' },
        { value: false, label: t('common.no') || 'No' }
    ], [t]);
    const customFilterRender = useMemo(() => ({
        timeOfPickup: (value, onChange) => (_jsx(HTimePicker, { value: value ?? null, handleSelectedTime: (iso) => {
                if (!iso) {
                    onChange(null);
                    return;
                }
                const m = iso.match(/T(\d{2}):(\d{2})/);
                onChange(m ? `${m[1]}:${m[2]}` : null);
            }, label: t('events.eventDetails.transportTab.timeOfPickup'), bg: 'grey8' }))
    }), [t]);
    useEffect(() => {
        if (eventId) {
            const request = {
                eventId,
                includeClosed: true,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined,
                Name: filters.pickupLocationName,
                address: filters.address,
                companyId: filters.companies,
                supplier: filters.supplier
            };
            if (isFamilyFest) {
                const sort = sortModel[0];
                const FamilyFestRequest = {
                    eventId,
                    includeClosed: true,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    Name: filters.pickupLocationName,
                    address: filters.address,
                    companyId: filters.companies,
                    supplierId: filters.supplier,
                    accommodationAvailable: filters.isAccommodationAvailable,
                    timeOfPickup: filters.timeOfPickup,
                    sortBy: sort?.field,
                    sortOrder: sort?.sort ?? 'desc'
                };
                dispatch(FamilyFestGetPickupLocationsPaginatedAsync(FamilyFestRequest));
            }
            else {
                dispatch(getAllPickupLocationsAsync(request));
            }
        }
    }, [
        dispatch,
        eventId,
        isFamilyFest,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        pickupLocationResponse?.data,
        familyFestCloseResponse?.data,
        filters,
        sortModel,
        selectedCompanyIds
    ]);
    const refreshPickupLocations = () => {
        if (!eventId)
            return;
        const request = {
            eventId,
            includeClosed: true,
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            searchString: searchValue || undefined,
            Name: filters.pickupLocationName,
            address: filters.address,
            companyId: filters.companies,
            supplier: filters.supplier,
            status: filters.status
        };
        if (isFamilyFest) {
            const sort = sortModel[0];
            const FamilyFestRequest = {
                eventId,
                includeClosed: true,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined,
                Name: filters.pickupLocationName,
                address: filters.address,
                companyId: filters.companies,
                supplierId: filters.supplier,
                status: filters.status,
                accommodationAvailable: filters.isAccommodationAvailable,
                timeOfPickup: filters.timeOfPickup,
                sortBy: sort?.field,
                sortOrder: sort?.sort ?? 'desc'
            };
            dispatch(FamilyFestGetPickupLocationsPaginatedAsync(FamilyFestRequest));
        }
        else {
            dispatch(getAllPickupLocationsAsync(request));
        }
    };
    const handleFilterClick = () => {
        setGridFilterOpen(true);
    };
    const handleExportCoordinators = async () => {
        if (!eventId)
            return;
        try {
            setIsExportingCoordinators(true);
            await dispatch(downloadFamilyFestPickupLocationCoordinatorsExcelAsync(eventId)).unwrap();
            toast.success(t('events.eventDetails.transportTab.exportCoordinatorsSuccess'));
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.transportTab.exportCoordinatorsFailed'));
        }
        finally {
            setIsExportingCoordinators(false);
        }
    };
    const handleExportPickupLocations = async () => {
        if (!eventId)
            return;
        try {
            setIsExportingPickupLocations(true);
            await dispatch(downloadFamilyFestPickupLocationsExcelAsync(eventId)).unwrap();
            toast.success(t('common.exportSuccess', 'Data exported successfully!'));
        }
        catch (error) {
            toast.error(error?.error || t('common.exportFailed', 'Failed to export data. Please try again.'));
        }
        finally {
            setIsExportingPickupLocations(false);
        }
    };
    const handleOpenExportMenu = (event) => {
        setExportMenuAnchorEl(event.currentTarget);
    };
    const handleCloseExportMenu = () => {
        setExportMenuAnchorEl(null);
    };
    const exportActions = [
        {
            text: t('events.eventDetails.transportTab.exportPickupLocations'),
            to: handleExportPickupLocations,
            group: true
        },
        {
            text: t('events.eventDetails.transportTab.exportCoordinators'),
            to: handleExportCoordinators,
            group: true
        }
    ];
    const handleOpenCloseDialog = (pickupLocationName, pickupLocationId) => {
        setSelectedPickupLocation(pickupLocationName);
        setSelectedPickupLocationId(pickupLocationId);
        setIsCloseDialogOpen(true);
    };
    const handleCloseDialog = () => {
        setIsCloseDialogOpen(false);
        setSelectedPickupLocation('');
    };
    const handleOpenRowMenu = (event, row) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };
    const handleCloseRowMenu = () => {
        setMenuAnchorEl(null);
        setSelectedRow(null);
    };
    const handleEditPickupLocation = useCallback((row) => {
        setEditingPickupLocation(row);
        setIsNewPickupDialogOpen(true);
    }, []);
    const handleCloseLocationFromMenu = useCallback(() => {
        if (!selectedRow)
            return;
        handleOpenCloseDialog(selectedRow.name, selectedRow.id);
        handleCloseRowMenu();
    }, [selectedRow]);
    useEffect(() => {
        const id = setTimeout(() => setDebouncedCoordinatorSearch(coordinatorSearch.trim()), 300);
        return () => clearTimeout(id);
    }, [coordinatorSearch]);
    const coordinatorTargetId = coordinatorActionTarget?.id;
    useEffect(() => {
        if (!isAssignCoordinatorDialogOpen || !coordinatorTargetId)
            return;
        let cancelled = false;
        setCoordinatorAttendeesLoading(true);
        dispatch(getAllFamilyFestPickupAssignmentsAsync({
            pickupLocationId: coordinatorTargetId,
            searchString: debouncedCoordinatorSearch || undefined
        }))
            .unwrap()
            .then((result) => {
            if (cancelled)
                return;
            setCoordinatorAttendeeRows(result.map((a) => ({
                id: a.id,
                name: a.name,
                company: a.companyName,
                type: a.attendeeType ?? '',
                primaryPerson: a.primaryAttendeeName || '-',
                registered: formatDMY(a.createdDate)
            })));
        })
            .catch(() => {
            if (!cancelled)
                setCoordinatorAttendeeRows([]);
        })
            .finally(() => {
            if (!cancelled)
                setCoordinatorAttendeesLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [isAssignCoordinatorDialogOpen, coordinatorTargetId, debouncedCoordinatorSearch, dispatch]);
    const handleOpenAssignCoordinatorDialog = useCallback(() => {
        if (!selectedRow)
            return;
        setCoordinatorSearch('');
        setDebouncedCoordinatorSearch('');
        setCoordinatorActionTarget(selectedRow);
        handleCloseRowMenu();
        setIsAssignCoordinatorDialogOpen(true);
    }, [selectedRow]);
    const handleAssignCoordinator = useCallback(async (assignmentId) => {
        if (!coordinatorActionTarget)
            return;
        try {
            await dispatch(FamilyFestAssignCoordinatorAsync({
                assignmentId,
                pickupLocationId: coordinatorActionTarget.id
            })).unwrap();
            toast.success(t('events.eventDetails.transportTab.coordinatorAssignedSuccessfully'));
            setIsAssignCoordinatorDialogOpen(false);
            setCoordinatorAttendeeRows([]);
            setCoordinatorActionTarget(null);
            refreshPickupLocations();
        }
        catch (error) {
            toast.error(error?.error || t('common.somethingWentWrong'));
        }
    }, [coordinatorActionTarget, dispatch, t]);
    const handleOpenUnassignCoordinatorConfirm = useCallback(() => {
        if (!selectedRow)
            return;
        setCoordinatorActionTarget(selectedRow);
        handleCloseRowMenu();
        setConfirmUnassignCoordinatorOpen(true);
    }, [selectedRow]);
    const handleConfirmUnassignCoordinator = useCallback(async () => {
        if (!coordinatorActionTarget)
            return;
        try {
            const result = await dispatch(getAllFamilyFestPickupAssignmentsAsync({ pickupLocationId: coordinatorActionTarget.id })).unwrap();
            const coordinatorAssignment = result.find((a) => a.isCoordinator);
            if (!coordinatorAssignment) {
                toast.error(t('common.somethingWentWrong'));
                return;
            }
            await dispatch(FamilyFestUnassignCoordinatorAsync({
                assignmentId: coordinatorAssignment.id,
                pickupLocationId: coordinatorActionTarget.id
            })).unwrap();
            toast.success(t('events.eventDetails.transportTab.coordinatorUnassignedSuccessfully'));
            refreshPickupLocations();
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.transportTab.coordinatorUnassignFailed'));
        }
        finally {
            setConfirmUnassignCoordinatorOpen(false);
            setCoordinatorActionTarget(null);
        }
    }, [coordinatorActionTarget, dispatch, t]);
    const rowActions = useMemo(() => {
        const isSelectedRowClosed = selectedRow?.isClosed || selectedRow?.status === PickUpLocationStatusEnum.Closed;
        const actions = [];
        if (!isSelectedRowClosed) {
            actions.push({
                text: t('events.eventDetails.transportTab.closePickupLocation'),
                icon: _jsx(Iconly, { name: 'CloseSquare', set: 'curved' }),
                to: handleCloseLocationFromMenu,
                group: true
            });
        }
        if (isFamilyFest && selectedRow && !isSelectedRowClosed) {
            if (selectedRow.isCoordinatorAssigned) {
                actions.push({
                    text: t('events.eventDetails.transportTab.unassignCoordinator'),
                    icon: _jsx(Iconly, { name: 'User', set: 'curved' }),
                    to: handleOpenUnassignCoordinatorConfirm,
                    group: true
                });
            }
            else {
                actions.push({
                    text: t('events.eventDetails.transportTab.assignCoordinator'),
                    icon: _jsx(Iconly, { name: 'User', set: 'curved' }),
                    to: handleOpenAssignCoordinatorDialog,
                    group: true
                });
            }
        }
        return actions;
    }, [
        t,
        handleCloseLocationFromMenu,
        handleOpenAssignCoordinatorDialog,
        handleOpenUnassignCoordinatorConfirm,
        isFamilyFest,
        selectedRow
    ]);
    const getStatusFromLocation = (location) => {
        if (location.status === PickUpLocationStatusEnum.Closed) {
            return 'Closed';
        }
        if (location.status === PickUpLocationStatusEnum.Overbooked) {
            return 'Overbooked';
        }
        if (location.status === PickUpLocationStatusEnum.Booked) {
            return 'Booked';
        }
        if (location.status === PickUpLocationStatusEnum.VacanciesLeft)
            return 'Vacancies left';
        if (location.status === PickUpLocationStatusEnum.New)
            return 'New';
        return 'New';
    };
    const formatDate = (dateString) => {
        if (!dateString)
            return '—';
        try {
            return dayjs(dateString).format('MMM DD, YYYY');
        }
        catch {
            return '—';
        }
    };
    const formatPickupTime = (timeOfPickup) => {
        if (!timeOfPickup)
            return '—';
        const value = String(timeOfPickup).trim();
        const match = /T(\d{2}):(\d{2})/.exec(value) ?? /^(\d{1,2}):(\d{2})/.exec(value);
        if (!match)
            return '—';
        return `${match[1].padStart(2, '0')}:${match[2]}`;
    };
    const columns = [
        {
            field: 'pickupLocationName',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.pickupLocationName'),
            flex: 1,
            minWidth: 200,
            valueGetter: (_value, row) => row.name || '',
            renderCell: (params) => {
                const location = params.row;
                return (_jsx(Typography, { variant: 'body2', children: location.name || '—' }));
            }
        },
        {
            field: 'address',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.pickupLocationAddress'),
            flex: 1.5,
            minWidth: 250,
            renderCell: (params) => params.row.description || '—'
        },
        ...(isFamilyFest
            ? [
                {
                    field: 'coordinatorName',
                    headerName: t('events.eventDetails.transportTab.coordinatorName'),
                    flex: 1,
                    minWidth: 160,
                    renderCell: (params) => params.row.coordinatorName || '—'
                }
            ]
            : []),
        // Conditionally include companies column
        ...(isExternalTransportManager
            ? []
            : [
                {
                    field: 'companies',
                    headerName: t('events.eventDetails.transportTab.companies'),
                    flex: 1,
                    minWidth: 150,
                    renderCell: (params) => {
                        const companies = params.row.eventCompanies;
                        if (!companies || companies.length === 0)
                            return '—';
                        const avatarItems = companies.map((company) => ({
                            id: company.id,
                            label: company.name,
                            image: company.logoUrl || undefined
                        }));
                        return (_jsx(GenericAvatarGroup, { items: avatarItems, maxDisplay: 3, size: 28, showTooltip: true }));
                    }
                }
            ]),
        ...(!isFamilyFest
            ? [
                {
                    field: 'supplier',
                    headerName: t('events.eventDetails.transportTab.supplier'),
                    flex: 1,
                    minWidth: 150,
                    renderCell: (params) => params.row.supplierName || '—'
                }
            ]
            : []),
        {
            field: 'isAccommodationAvailable',
            headerName: t('events.eventDetails.transportTab.accommodationAvailable'),
            flex: 0.9,
            minWidth: 150,
            renderCell: (params) => params.row.isAccommodationAvailable
                ? t('common.yes') || 'Yes'
                : t('common.no') || 'No'
        },
        {
            field: 'timeOfPickup',
            headerName: t('events.eventDetails.transportTab.timeOfPickup'),
            flex: 0.9,
            minWidth: 140,
            renderCell: (params) => formatPickupTime(params.row.timeOfPickup)
        },
        ...(isFamilyFest ? [
            {
                field: 'totalAttendeesCount',
                headerName: t('events.eventDetails.transportTab.totalAttendees'),
                flex: 0.9,
                minWidth: 130,
                renderCell: (params) => params.row.totalAttendeesCount ?? '—'
            },
            {
                field: 'wheelchairCount',
                headerName: t('events.eventDetails.transportTab.wheelchairCount'),
                flex: 0.9,
                minWidth: 130,
                renderCell: (params) => params.row.wheelchairCount ?? '—'
            }
        ] : []),
        ...(!isFamilyFest ? [{
                field: 'capacity',
                headerName: t('events.eventDetails.transportTab.seats'),
                flex: 0.8,
                minWidth: 100,
                valueGetter: (_value, row) => {
                    const { assignedAttendees, totalCapacity } = row;
                    if (assignedAttendees == null || totalCapacity == null)
                        return -1;
                    return assignedAttendees;
                },
                renderCell: (params) => {
                    const location = params.row;
                    if (location.totalCapacity == null || location.assignedAttendees == null)
                        return '—';
                    const capacityText = `${location.assignedAttendees}/${location.totalCapacity}`;
                    if (!isFamilyFest)
                        return capacityText;
                    const tooltipContent = (_jsx(Box, { component: 'span', sx: { display: 'block' }, children: _jsxs(Box, { component: 'ul', sx: { m: 0, pl: 2, '& li': { py: 0.25 } }, children: [_jsxs("li", { style: { ...theme.typography.caption }, children: [t('events.eventDetails.transportTab.totalSeats'), ": ", location.totalCapacity] }), _jsxs("li", { style: { ...theme.typography.caption }, children: [t('events.eventDetails.transportTab.assigned'), ": ", location.assignedAttendees] })] }) }));
                    return (_jsx(Tooltip, { title: tooltipContent, placement: 'top', slotProps: {
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        bgcolor: 'grey.900',
                                        color: 'common.white',
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        typography: 'caption'
                                    }
                                }
                            }
                        }, children: _jsx(Box, { component: 'span', sx: { cursor: 'default' }, children: capacityText }) }));
                }
            }] : []),
        // Conditionally include actions column
        ...(isExternalTransportManager
            ? []
            : [
                {
                    field: 'actions',
                    headerName: '',
                    flex: 0.5,
                    minWidth: 100,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => {
                        // Every menu action is hidden for a closed location, so the menu would open empty.
                        const isClosed = params.row.isClosed || params.row.status === PickUpLocationStatusEnum.Closed;
                        if (isClosed)
                            return null;
                        return (_jsxs(Box, { display: 'flex', gap: 1, alignItems: 'center', children: [_jsx(IconButton, { color: 'inherit', onClick: (event) => {
                                        event.stopPropagation();
                                        handleEditPickupLocation(params.row);
                                    }, children: _jsx(Iconly, { name: 'Edit', set: 'curved' }) }), _jsx(IconButton, { color: 'inherit', onClick: (event) => handleOpenRowMenu(event, params.row), children: _jsx(MoreHoriz, {}) })] }));
                    }
                }
            ])
    ];
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: pickupLocations?.items || [], showHeaderControls: false, showNewHeaderControls: true, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, filterOptions: {
                    companies: companyFilterOptions,
                    supplier: supplierFilterOptions,
                    isAccommodationAvailable: accommodationFilterOptions
                }, customFilterRender: customFilterRender, resetFiltersKey: resetFiltersKey, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, rowCount: pickupLocations?.totalCount || 0, pageSizeOptions: [10, 25, 50, 100], maxHeight: 'calc(100dvh - 240px)', onRowClick: (params) => {
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('tab', 'transport');
                        next.set('locationId', String(params.row.id));
                        return next;
                    });
                }, noRowsText: t('events.eventDetails.transportTab.noPickupLocationsFound'), customButton: _jsxs(Box, { display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', gap: isSmallScreen ? '12px' : 0, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.transportTab.pickupLocations') }), isSmallScreen && isFamilyFest && (_jsx(Button, { startIcon: _jsx(ExportSquare, {}), variant: 'outlined', size: 'small', color: 'primary', onClick: handleOpenExportMenu, disabled: isExportingCoordinators || isExportingPickupLocations, children: t('events.eventDetails.transportTab.export') })), isSmallScreen && !isExternalTransportManager && (_jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: isSmallScreen ? 'small' : 'large', color: 'primary', onClick: () => {
                                        setEditingPickupLocation(null);
                                        setIsNewPickupDialogOpen(true);
                                    }, children: t('events.eventDetails.transportTab.newPickupLocation') }))] }), !isSmallScreen && (_jsxs(Box, { display: 'flex', gap: '12px', children: [_jsx(SearchFilter, { searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('placeholders.search'), onFilterClick: handleFilterClick, filterLabel: t('events.filters'), breakpoint: 'lg', onClear: () => {
                                        setSearchValue('');
                                    }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                        setFilters({});
                                        setResetFiltersKey((k) => k + 1);
                                    }, isDatagrid: true, widthBox: '50%', positonTop: '-9px' }), isFamilyFest && (_jsx(Button, { startIcon: _jsx(ExportSquare, {}), variant: 'outlined', size: 'large', color: 'primary', onClick: handleOpenExportMenu, disabled: isExportingCoordinators || isExportingPickupLocations, children: t('events.eventDetails.transportTab.export') })), !isExternalTransportManager && (_jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: isSmallScreen ? 'small' : 'large', color: 'primary', onClick: () => {
                                        setEditingPickupLocation(null);
                                        setIsNewPickupDialogOpen(true);
                                    }, children: t('events.eventDetails.transportTab.newPickupLocation') }))] })), isSmallScreen && (_jsx(Box, { display: 'flex', children: _jsx(SearchFilter, { fullWidth: true, forceDesktopVariant: true, searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('placeholders.search'), onFilterClick: handleFilterClick, filterLabel: t('events.filters'), breakpoint: 'lg', onClear: () => {
                                    setSearchValue('');
                                }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                    setFilters({});
                                    setResetFiltersKey((k) => k + 1);
                                }, isDatagrid: true, widthBox: '50%', positonTop: '-9px' }) }))] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(ClosePickupLocationDialog, { open: isCloseDialogOpen, onClose: handleCloseDialog, pickupLocationName: selectedPickupLocation, locationId: selectedPickupLocationId, onCloseSuccess: refreshPickupLocations }), _jsx(NewPickupLocationDialog, { open: isNewPickupDialogOpen, onClose: () => {
                    setIsNewPickupDialogOpen(false);
                    setEditingPickupLocation(null);
                }, onSuccess: () => {
                    refreshPickupLocations();
                    setEditingPickupLocation(null);
                }, editingPickupLocation: editingPickupLocation }), isFamilyFest && (_jsxs(_Fragment, { children: [_jsx(AssignPickupLocationCoordinatorDialog, { open: isAssignCoordinatorDialogOpen, loading: coordinatorAttendeesLoading, onClose: () => {
                            setIsAssignCoordinatorDialogOpen(false);
                            setCoordinatorAttendeeRows([]);
                            setCoordinatorActionTarget(null);
                        }, rows: coordinatorAttendeeRows, onAssignCoordinator: handleAssignCoordinator, searchValue: coordinatorSearch, onSearchChange: setCoordinatorSearch }), _jsx(ConfirmationDialog, { open: confirmUnassignCoordinatorOpen, onClose: () => {
                            setConfirmUnassignCoordinatorOpen(false);
                            setCoordinatorActionTarget(null);
                        }, title: t('events.eventDetails.transportTab.confirmUnassignCoordinatorTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                        setConfirmUnassignCoordinatorOpen(false);
                                        setCoordinatorActionTarget(null);
                                    }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'inherit', onClick: handleConfirmUnassignCoordinator, children: t('actions.confirm') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('events.eventDetails.transportTab.confirmUnassignCoordinatorBody', {
                                    name: coordinatorActionTarget?.coordinatorName ?? ''
                                }) }) }) })] })), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleCloseRowMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, actions: rowActions }), _jsx(StyledMenu, { anchorEl: exportMenuAnchorEl, open: Boolean(exportMenuAnchorEl), onClose: handleCloseExportMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, actions: exportActions })] }));
};
export default LocationContent;
